import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, Eye, EyeOff, Loader2, CheckCircle, Shield, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, loading: authLoading, setUser } = useAuth();
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // First-time password set: skip the "current password" field. The session
  // is proof of identity (established via secure magic-link or explicit login).
  const isFirstTime = !!user?.must_change_password;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.new_password) {
      toast.error("Please enter a new password");
      return;
    }
    if (!isFirstTime && !formData.current_password) {
      toast.error("Please enter your current password");
      return;
    }
    if (formData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (!isFirstTime && formData.current_password === formData.new_password) {
      toast.error("New password must be different from current password");
      return;
    }

    setLoading(true);
    try {
      const payload = isFirstTime
        ? { new_password: formData.new_password }
        : {
            current_password: formData.current_password,
            new_password: formData.new_password,
          };
      await api.post("/auth/change-password", payload);

      toast.success(
        isFirstTime
          ? "Welcome to Credentia AI! Your password has been set."
          : "Password changed successfully!"
      );

      // Refresh user state so must_change_password becomes false
      if (setUser && user) {
        setUser({ ...user, must_change_password: false });
      }

      // Redirect based on user role
      if (user?.role === "candidate") {
        navigate("/my-application", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      console.error("Password change failed:", error);
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      {authLoading ? (
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-slate-700 mx-auto" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      ) : (
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isFirstTime ? "bg-blue-100" : "bg-amber-100"
              }`}
            >
              {isFirstTime ? (
                <Sparkles className="w-8 h-8 text-blue-600" />
              ) : (
                <Shield className="w-8 h-8 text-amber-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {isFirstTime ? "Set Your Password" : "Change Your Password"}
            </CardTitle>
            <CardDescription>
              {isFirstTime
                ? `Welcome${user?.name ? `, ${user.name}` : ""}! Choose a secure password for your Credentia AI account.`
                : "Update your account password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              autoComplete="on"
            >
              {!isFirstTime && (
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="current_password"
                      name="current-password"
                      data-testid="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Enter your current password"
                      value={formData.current_password}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          current_password: e.target.value,
                        })
                      }
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label="Toggle password visibility"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="new_password">
                  {isFirstTime ? "New Password" : "New Password"}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new_password"
                    name="new-password"
                    data-testid="new-password"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Min 6 characters"
                    value={formData.new_password}
                    onChange={(e) =>
                      setFormData({ ...formData, new_password: e.target.value })
                    }
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label="Toggle password visibility"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm_password"
                    name="confirm-password"
                    data-testid="confirm-password"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Re-enter new password"
                    value={formData.confirm_password}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirm_password: e.target.value,
                      })
                    }
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
                data-testid="submit-password"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isFirstTime ? "Setting Password..." : "Changing Password..."}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {isFirstTime ? "Set Password & Continue" : "Change Password"}
                  </>
                )}
              </Button>
            </form>

            {!isFirstTime && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate(-1)}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Cancel and go back
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
